import { getDefaultLocale } from '../../localization/translations';
import type { ExampleQuestionCategory } from '../../types/exampleQuestions';

type CachePayload = {
    categories?: ExampleQuestionCategory[];
    timestamp?: number;
};

export type ExampleQuestionsCacheStatus = 'hit' | 'miss' | 'expired' | 'corrupt';

export type ExampleQuestionsCacheReadResult = {
    status: ExampleQuestionsCacheStatus;
    categories: ExampleQuestionCategory[];
};

export const DEFAULT_EXAMPLE_QUESTIONS_CACHE_TTL_MS = 60 * 60 * 1000;

const cacheKeyPrefix = 'example-prompts';
const defaultLocale = getDefaultLocale();
const emptyCategories: ExampleQuestionCategory[] = [];

export function normalizeExampleQuestionsCacheTtlMs(cacheTtlMs?: number): number {
    if (cacheTtlMs === undefined || !Number.isFinite(cacheTtlMs) || cacheTtlMs <= 0) {
        return DEFAULT_EXAMPLE_QUESTIONS_CACHE_TTL_MS;
    }

    return cacheTtlMs;
}

export function normalizeExampleEndpointForCache(endpoint?: string | null): string | undefined {
    const trimmed = endpoint?.trim();
    if (!trimmed) {
        return undefined;
    }

    try {
        const url = new URL(trimmed);
        url.hash = '';

        const searchEntries = [...url.searchParams.entries()].sort(([leftKey, leftValue], [rightKey, rightValue]) => {
            const keyComparison = leftKey.localeCompare(rightKey);
            if (keyComparison !== 0) {
                return keyComparison;
            }

            return leftValue.localeCompare(rightValue);
        });

        url.search = '';
        for (const [key, value] of searchEntries) {
            url.searchParams.append(key, value);
        }

        return url.toString();
    } catch {
        return trimmed;
    }
}

export function buildExampleQuestionsCacheKey(endpoint?: string | null, language?: string | null): string | null {
    const normalizedEndpoint = normalizeExampleEndpointForCache(endpoint);
    if (!normalizedEndpoint) {
        return null;
    }

    const normalizedLanguage = language?.trim() || defaultLocale;
    return `${cacheKeyPrefix}::${normalizedEndpoint}::${normalizedLanguage}`;
}

export function readExampleQuestionsCache(cacheKey: string | null, cacheTtlMs?: number): ExampleQuestionsCacheReadResult {
    if (!cacheKey || !hasLocalStorage()) {
        return createReadResult('miss');
    }

    try {
        const raw = window.localStorage.getItem(cacheKey);
        if (!raw) {
            return createReadResult('miss');
        }

        const parsed = JSON.parse(raw) as CachePayload;
        const timestamp = parsed?.timestamp;
        if (!Array.isArray(parsed?.categories) || typeof timestamp !== 'number' || !Number.isFinite(timestamp)) {
            removeExampleQuestionsCache(cacheKey);
            return createReadResult('corrupt');
        }

        const normalizedCacheTtlMs = normalizeExampleQuestionsCacheTtlMs(cacheTtlMs);
        if (Date.now() - timestamp > normalizedCacheTtlMs) {
            removeExampleQuestionsCache(cacheKey);
            return createReadResult('expired');
        }

        return {
            status: 'hit',
            categories: parsed.categories
        };
    } catch {
        removeExampleQuestionsCache(cacheKey);
        return createReadResult('corrupt');
    }
}

export function writeExampleQuestionsCache(cacheKey: string | null, categories: ExampleQuestionCategory[]): void {
    if (!cacheKey || !hasLocalStorage()) {
        return;
    }

    try {
        window.localStorage.setItem(
            cacheKey,
            JSON.stringify({
                categories,
                timestamp: Date.now()
            })
        );
    } catch {
        // ignore cache write errors
    }
}

export function removeExampleQuestionsCache(cacheKey: string | null): void {
    if (!cacheKey || !hasLocalStorage()) {
        return;
    }

    try {
        window.localStorage.removeItem(cacheKey);
    } catch {
        // ignore cache remove errors
    }
}

function createReadResult(status: ExampleQuestionsCacheStatus): ExampleQuestionsCacheReadResult {
    return {
        status,
        categories: emptyCategories
    };
}

function hasLocalStorage(): boolean {
    return typeof window !== 'undefined' && typeof window.localStorage !== 'undefined';
}
